import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { getFormData } from '../shared/helpers/utils';
import { EntitiesHttpService } from './entities-http.service';
import { Application_FormProvider } from './application-form-provider';
import { SelectItem } from '../shared/interfaces/select-item.interface';
import { confirmMessage } from '../shared/helpers/messages-popup';
import { COUNTRY_LIST } from '../shared/constants/countryList.const';
import { TradeHttpService } from '../transactions/services/trade-http.service';

@Component({
  selector: 'app-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss'],
})
export class ApplicationComponent implements OnInit, OnDestroy {
  @ViewChild('fileUploader_bank') fileUploader_bank: ElementRef;
  @ViewChild('fileUploader_address') fileUploader_address: ElementRef;
  @ViewChild('fileUploader_id') fileUploader_id: ElementRef;
  entityForm: FormGroup;
  entityForm2: FormGroup;
  currentEntity: any;
  countriesList = COUNTRY_LIST;
  banksList: SelectItem[] = [];
  currentStep = 1;
  totalSteps = 2;
  steps: string[] = ['Personal Details', 'Other Details'];
  entityCreated = true;
  uploadedFiles: any;
  showUploadLoader_bank = false;
  showUploadLoader_address = false;
  showUploadLoader_id = false;
  isSelected_bank = false;
  isSelected_address = false;
  isSelected_id = false;
  isBankError = false;
  reloaded = false;
  constructor(
    private formProvider: Application_FormProvider,
    private entitiesHttpService: EntitiesHttpService,
    private tradeTransactionsHttpService: TradeHttpService,
  ) {}

  preventCharcter(event: any): void {
    const inputValue = event.target.value;
    const cleanedValue = inputValue.replace(/[^0-9+-]/g, '');
    event.target.value = cleanedValue;
    return cleanedValue;
  }
  ngOnInit(): void {
    this.entityForm = this.formProvider.initFormStep1();
    this.entityForm2 = this.formProvider.initFormStep2();
    this.getBanksList();
  }

  SubmitForm() {
    if (this.isBankError) {
      this.callBankDataAgain();
    } else {
      this.submitFormWithType(1);
      this.reloaded = false;
    }
  }

  submitFormWithType(type: number) {
    this.entityForm.patchValue({ type });
    if (this.entityForm.invalid || this.entityForm2.invalid) return;
    const rawData = getFormData({
      ...this.entityForm.value,
      mobile: `${this.entityForm.value.mobileCountryCode || ''}${this.entityForm.value.mobileAreaCode || ''}${this.entityForm.value.mobileNumber || ''}`,
      office: `${this.entityForm.value.phoneCountryCode || ''}${this.entityForm.value.phoneAreaCode || ''}${this.entityForm.value.phoneNumber || ''}`,
      name: `${this.entityForm.value.firstName} ${this.entityForm.value.lastName}`,
    });

    this.doCreate(rawData);
  }

  private buildEntityBanksBody(rawData: any) {
    return {
      bankId: rawData.bankId || undefined,
      name: rawData.bankName || undefined,
      swiftBic: rawData.swiftBic || undefined,
      sortCode: rawData.sortCode || undefined,
      account: rawData.accountNumber?.toString() || undefined,
      iban: rawData.iban || undefined,
      bankStatementFile: Number(rawData.bankStatementFile) || undefined,
    };
  }

  private createBankAccount(data: any) {
    return this.entitiesHttpService.createEntityBanksAccounts({
      entityRoleId: this.currentEntity?.entityRoles[0].id,
      data,
    });
  }

  nextStep(): void {
    if (this.currentStep >= this.totalSteps) return;
    this.currentStep++;
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  onSubmitFirst() {
    if (this.entityForm.invalid) return;
    const rawData = getFormData({
      ...this.entityForm.value,
      sortCode: this.entityForm.value.sortCode?.toString().replace(/-/g, ''),
      mobile: `${this.entityForm.value.mobileCountryCode || ''}${this.entityForm.value.mobileAreaCode || ''}${this.entityForm.value.mobileNumber || ''}`,
      office: `${this.entityForm.value.phoneCountryCode || ''}${this.entityForm.value.phoneAreaCode || ''}${this.entityForm.value.phoneNumber || ''}`,
      name: `${this.entityForm.value.firstName} ${this.entityForm.value.lastName}`,
    });
    this.doCreate(rawData);
  }

  doCreate(data: any): void {
    const rawData2 = getFormData({ ...this.entityForm2.value });
    const body = {
      type: data.type,
      name: data.name,
      tradeName: data.tradingAs,
      roleName: data.roleName,
      entityRoleId: data.entityRoleId,
      firstName: data.firstName,
      lastName: data.lastName,
      idProofFile: Number(rawData2['idProofFile']),
    };

    if (this.entityCreated) {
      if (body.type === 1) {
        body.name = data.organisation;
        delete body.firstName;
        delete body.lastName;
      }
      this.entitiesHttpService.createEntity(body).subscribe({
        next: (response: any) => {
          this.currentEntity = response;
          this.submitFormWithType(2);
          if (response.type == 2) {
            this.doUpdate(data);
          }

          this.entityCreated = false;
        },
        error: (err) => {
          this.entityCreated = true;
          Swal.fire({
            icon: 'error',
            text:
              err.error?.message ||
              'An error occurred while processing the Personal Details.',
            showConfirmButton: false,
            timer: 5000,
          });
        },
      });
    }
  }

  doUpdate(data: any): boolean {
    const rawData2 = getFormData({
      ...this.entityForm2.value,
      sortCode: this.entityForm2.value.sortCode?.toString().replace(/-/g, ''),
    });
    const entityAddressBody = {
      country: data.country,
      state: data.state,
      city: data.city,
      area: data.area,
      lineOne: data.address1,
      lineTwo: data.address2,
      zipCode: data.postCode,
      countryCode: 'Arm',
      addressProofFile: Number(rawData2['addressProofFile']) || null,
    };
    const entityPropertiesBody = {
      emails: data.email ? [data.email] : undefined,
      phoneNumbers: data.mobile
        ? {
            office: data.office,
            mobile: data.mobile,
          }
        : undefined,
    };
    const entityBanksBody = this.buildEntityBanksBody(rawData2);
    const requests = [];
    if (Object.keys(entityAddressBody).length) {
      requests.push(this.updateAddressData(entityAddressBody));
    }

    if (Object.keys(entityPropertiesBody).length) {
      requests.push(this.updateProperties(entityPropertiesBody));
    }
    if (Object.keys(entityBanksBody).length) {
      requests.push(this.createBankAccount(entityBanksBody));
    }

    if (!requests.length) return false;

    forkJoin(requests).subscribe({
      next: () => {
        if (data.type == 2) {
          this.currentStep = 1;
          this.entityForm.reset();
          this.entityForm2.reset();
          this.entityForm = this.formProvider.initFormStep1();
          this.entityForm2 = this.formProvider.initFormStep2();
          if (this.fileUploader_bank) {
            this.fileUploader_bank.nativeElement.value = '';
            this.isSelected_bank = false;
          }
          if (this.fileUploader_address) {
            this.fileUploader_address.nativeElement.value = '';
            this.isSelected_address = false;
          }
          if (this.fileUploader_id) {
            this.fileUploader_id.nativeElement.value = '';
            this.isSelected_id = false;
          }
          Swal.fire({
            icon: 'success',
            text: `Details successfully added`,
            showConfirmButton: false,
            timer: 3000,
          });
          this.entityCreated = true;
        }
        if (!this.reloaded) {
          this.reloaded = true;
        }
      },
      error: (err) => {
        this.entityCreated = false;
        this.isBankError = true;
        Swal.fire({
          icon: 'error',
          text:
            err.error?.message ||
            'An error occurred while processing the entity.',
          showConfirmButton: false,
          timer: 5000,
        });
      },
    });
    return true;
  }

  callBankDataAgain() {
    const rawData2 = getFormData({
      ...this.entityForm2.value,
      sortCode: this.entityForm2.value.sortCode?.toString().replace(/-/g, ''),
    });
    const entityBanksBody = this.buildEntityBanksBody(rawData2);
    const requests = [];

    if (Object.keys(entityBanksBody).length) {
      requests.push(this.createBankAccount(entityBanksBody));
    }

    if (requests.length == 0) {
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.currentStep = 1;
        this.entityForm.reset();
        this.entityForm2.reset();
        this.entityForm = this.formProvider.initFormStep1();
        this.entityForm2 = this.formProvider.initFormStep2();
        if (this.fileUploader_bank) {
          this.fileUploader_bank.nativeElement.value = '';
          this.isSelected_bank = false;
        }
        if (this.fileUploader_address) {
          this.fileUploader_address.nativeElement.value = '';
          this.isSelected_address = false;
        }
        if (this.fileUploader_id) {
          this.fileUploader_id.nativeElement.value = '';
          this.isSelected_id = false;
        }
        Swal.fire({
          icon: 'success',
          text: `Details successfully added`,
          showConfirmButton: false,
          timer: 3000,
        });
        this.entityCreated = true;
        this.isBankError = false;
      },
      error: (err) => {
        this.entityCreated = false;
        this.isBankError = true;
        Swal.fire({
          icon: 'error',
          text:
            err.error?.message ||
            'An error occurred while processing the entity.',
          showConfirmButton: false,
          timer: 5000,
        });
      },
    });
  }
  private updateAddressData(data: any) {
    return this.entitiesHttpService.updateEntityAddress({
      entityId: this.currentEntity.id,
      data,
    });
  }

  private updateProperties(data: any) {
    return this.entitiesHttpService.updateEntityProperties({
      entityId: this.currentEntity.id,
      data,
    });
  }

  private getBanksList(): void {
    this.entitiesHttpService.getBanksList().subscribe((response) => {
      this.banksList = response;
    });
  }

  handleBankChange(event: any) {
    this.entityForm2.get('bankName')?.patchValue(event?.name || null);
  }

  formatPhoneNumber(event: Event) {
    let input = (event.target as HTMLInputElement).value.replace(/\D/g, '');
    if (input.length > 10) input = input.slice(0, 10);
    if (isNaN(Number(input))) {
      (event.target as HTMLInputElement).value = '';
      return;
    }
    if (input.length <= 3) {
      (event.target as HTMLInputElement).value = input;
    } else if (input.length <= 6) {
      (event.target as HTMLInputElement).value =
        `${input.slice(0, 3)} - ${input.slice(3)}`;
    } else {
      (event.target as HTMLInputElement).value =
        `${input.slice(0, 3)} - ${input.slice(3, 6)} - ${input.slice(6)}`;
    }
  }

  formatSortCode(event: any) {
    let value = event.target.value.replace(/\D/g, '');
    value = value.match(/.{1,2}/g)?.join('-') || '';

    event.target.value = value;
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  fileChange(element: any, type: string): void {
    this.uploadedFiles = element.target.files[0];
    const maxSize = 10000000; // 10 MB
    const allowedFileType = 'application/pdf';
    const file = element.target.files[0];
    if (!file) {
      this.showUploadLoader_bank = false;
      this.showUploadLoader_address = false;
      this.showUploadLoader_id = false;
      this.uploadedFiles = null;
      element.target.value = '';
      return;
    }

    if (file.type !== allowedFileType) {
      this.uploadedFiles = null;
      element.target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: Only PDF files are allowed.',
        showConfirmButton: true,
        timer: 5000,
      });
      return;
    }

    if (file.size > maxSize) {
      this.uploadedFiles = null;
      element.target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: File size must be less than 10 MB.',
        showConfirmButton: true,
        timer: 5000,
      });

      return;
    }

    switch (type) {
      case 'bankStatementFile':
        this.isSelected_bank = true;
        this.showUploadLoader_bank = true;

        break;
      case 'addressProofFile':
        this.isSelected_address = true;
        this.showUploadLoader_address = true;
        break;
      case 'idProofFile':
        this.isSelected_id = true;
        this.showUploadLoader_id = true;
        break;
    }

    this.tradeTransactionsHttpService.uploadFile(file).subscribe(
      (response: any) => {
        this.updateFormAfterUploadSuccess(type, response);
        this.uploadedFiles = null;
      },
      (error) => {
        let errorMessage = error.error.message;
        if (Array.isArray(error)) {
          errorMessage = this.generateStringFromArrayWithLineBreak(error);
        }
        Swal.fire({
          icon: 'error',
          text: `${errorMessage}`,
          showConfirmButton: true,
          timer: 5000,
        });
        element.target.value = '';
        this.uploadedFiles = null;
        this.handleFileUploadError(type, error);
      },
    );
  }

  private updateFormAfterUploadSuccess(type: string, response: any): void {
    switch (type) {
      case 'bankStatementFile':
        this.showUploadLoader_bank = false;
        this.entityForm2.get('bankStatementFile')?.setValue(response?.id);
        break;
      case 'addressProofFile':
        this.showUploadLoader_address = false;
        this.entityForm2.get('addressProofFile')?.setValue(response?.id);
        break;
      case 'idProofFile':
        this.showUploadLoader_id = false;
        this.entityForm2.get('idProofFile')?.setValue(response?.id);
        break;
    }
  }

  private handleFileUploadError(type: string, error: any): void {
    const emptySring = '';
    switch (type) {
      case 'bankStatementFile':
        this.showUploadLoader_bank = false;
        this.entityForm2.get('bankStatementFile')?.setValue(emptySring);
        break;
      case 'addressProofFile':
        this.showUploadLoader_address = false;
        this.entityForm2.get('addressProofFile')?.setValue(emptySring);
        break;
      case 'idProofFile':
        this.showUploadLoader_id = false;
        this.entityForm2.get('idProofFile')?.setValue(emptySring);
        break;
    }
  }

  generateStringFromArrayWithLineBreak(array: string[]): string {
    let result = '';
    array.forEach((item, index) => {
      result += item;
      if (index < array.length - 1) {
        result += '<hr></br>';
      }
    });
    return result;
  }

  clearFileInput(fileUploader: HTMLInputElement, type: string): void {
    confirmMessage(' ').then((result) => {
      if (result.isConfirmed) {
        fileUploader.value = '';
        switch (type) {
          case 'bankStatementFile':
            this.isSelected_bank = false;
            this.entityForm2.get('bankStatementFile')?.setValue(null);
            break;
          case 'addressProofFile':
            this.isSelected_address = false;
            this.entityForm2.get('addressProofFile')?.setValue(null);
            break;
          case 'idProofFile':
            this.isSelected_id = false;
            this.entityForm2.get('idProofFile')?.setValue(null);
            break;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.entityForm.reset();
    this.entityForm2.reset();
  }
}
